"use client";

import { useEffect, useMemo, useRef, useState } from "react";

export type TableSortDirection = "asc" | "desc";

export type TableSortState<TColumn extends string> = {
  column: TColumn;
  direction: TableSortDirection;
} | null;

type TableColumnBase<TColumn extends string> = {
  id: TColumn;
  minWidth: number;
  width: number;
};

type TablePreferences<TColumn extends string> = {
  order?: TColumn[];
  sort?: TableSortState<TColumn>;
  visible?: TColumn[];
  widths?: Partial<Record<TColumn, number>>;
};

export function usePersistentTablePreferences<TColumn extends string>({
  columns,
  defaultVisibleColumns,
  storageKey,
}: {
  columns: Array<TableColumnBase<TColumn>>;
  defaultVisibleColumns?: TColumn[];
  storageKey: string;
}) {
  const defaultOrder = useMemo(
    () => columns.map((column) => column.id),
    [columns],
  );
  const defaultVisible = useMemo(
    () =>
      defaultVisibleColumns?.length
        ? defaultVisibleColumns.filter((column) =>
            defaultOrder.includes(column),
          )
        : defaultOrder,
    [defaultOrder, defaultVisibleColumns],
  );
  const defaultWidths = useMemo(
    () =>
      Object.fromEntries(
        columns.map((column) => [column.id, column.width]),
      ) as Record<TColumn, number>,
    [columns],
  );
  const minWidths = useMemo(
    () =>
      Object.fromEntries(
        columns.map((column) => [column.id, column.minWidth]),
      ) as Record<TColumn, number>,
    [columns],
  );
  const hydratedRef = useRef(false);
  const [columnOrder, setColumnOrder] = useState(() => defaultOrder);
  const [columnWidths, setColumnWidths] = useState(() => defaultWidths);
  const [sort, setSort] = useState<TableSortState<TColumn>>(null);
  const [visibleColumns, setVisibleColumns] = useState(() => defaultVisible);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const storedPreferences = readStoredPreferences<TColumn>(storageKey);

      setColumnOrder(normalizeOrder(storedPreferences?.order, defaultOrder));
      setColumnWidths(
        normalizeWidths(storedPreferences?.widths, defaultWidths, minWidths),
      );
      setSort(normalizeSort(storedPreferences?.sort, defaultOrder));
      setVisibleColumns(normalizeVisibleColumns(storedPreferences?.visible, defaultOrder, defaultVisible));
      hydratedRef.current = true;
    }, 0);

    return () => window.clearTimeout(timer);
  }, [defaultOrder, defaultVisible, defaultWidths, minWidths, storageKey]);

  function persist(nextPreferences: TablePreferences<TColumn>) {
    if (!hydratedRef.current) {
      return;
    }

    window.localStorage.setItem(
      storageKey,
      JSON.stringify({
        order: columnOrder,
        sort,
        visible: visibleColumns,
        widths: columnWidths,
        ...nextPreferences,
      }),
    );
  }

  function resizeColumn(column: TColumn, width: number) {
    setColumnWidths((current) => {
      const nextWidths = {
        ...current,
        [column]: Math.max(width, minWidths[column]),
      };
      persist({ widths: nextWidths });
      return nextWidths;
    });
  }

  function moveColumn(source: TColumn, target: TColumn) {
    if (source === target) {
      return;
    }

    setColumnOrder((current) => {
      const sourceIndex = current.indexOf(source);
      const targetIndex = current.indexOf(target);

      if (sourceIndex < 0 || targetIndex < 0) {
        return current;
      }

      const nextOrder = [...current];
      nextOrder.splice(sourceIndex, 1);
      nextOrder.splice(targetIndex, 0, source);
      persist({ order: nextOrder });
      return nextOrder;
    });
  }

  function toggleSort(column: TColumn) {
    setSort((current) => {
      const nextSort: TableSortState<TColumn> =
        current?.column === column
          ? { column, direction: current.direction === "asc" ? "desc" : "asc" }
          : { column, direction: "asc" };
      persist({ sort: nextSort });
      return nextSort;
    });
  }

  function toggleColumnVisibility(column: TColumn) {
    setVisibleColumns((current) => {
      const isVisible = current.includes(column);
      const nextVisible = isVisible
        ? current.filter((item) => item !== column)
        : [...current, column];

      if (!nextVisible.length) {
        return current;
      }

      persist({ visible: nextVisible });
      return nextVisible;
    });
  }

  function resetPreferences() {
    window.localStorage.removeItem(storageKey);
    setColumnOrder(defaultOrder);
    setColumnWidths(defaultWidths);
    setSort(null);
    setVisibleColumns(defaultVisible);
  }

  return {
    columnOrder,
    columnWidths,
    moveColumn,
    resetPreferences,
    resizeColumn,
    sort,
    toggleColumnVisibility,
    toggleSort,
    visibleColumns,
  };
}

function normalizeOrder<TColumn extends string>(
  order: TColumn[] | undefined,
  defaultOrder: TColumn[],
) {
  if (!order?.length) {
    return defaultOrder;
  }

  const known = new Set(defaultOrder);
  const normalized = order.filter((column) => known.has(column));
  const missing = defaultOrder.filter((column) => !normalized.includes(column));

  return [...normalized, ...missing];
}

function normalizeWidths<TColumn extends string>(
  widths: Partial<Record<TColumn, number>> | undefined,
  defaultWidths: Record<TColumn, number>,
  minWidths: Record<TColumn, number>,
) {
  return Object.fromEntries(
    Object.entries(defaultWidths).map(([column, width]) => {
      const key = column as TColumn;
      const nextWidth = Number(widths?.[key] ?? width);

      return [key, Math.max(nextWidth, minWidths[key])];
    }),
  ) as Record<TColumn, number>;
}

function normalizeSort<TColumn extends string>(
  sort: TableSortState<TColumn> | undefined,
  defaultOrder: TColumn[],
): TableSortState<TColumn> {
  if (!sort || !defaultOrder.includes(sort.column)) {
    return null;
  }

  return {
    column: sort.column,
    direction: sort.direction === "desc" ? "desc" : "asc",
  };
}

function normalizeVisibleColumns<TColumn extends string>(
  visible: TColumn[] | undefined,
  defaultOrder: TColumn[],
  defaultVisible: TColumn[],
) {
  if (!visible?.length) {
    return defaultVisible;
  }

  const known = new Set(defaultOrder);
  const normalized = visible.filter((column) => known.has(column));

  return normalized.length ? normalized : defaultOrder;
}

function readStoredPreferences<TColumn extends string>(storageKey: string) {
  if (typeof window === "undefined") {
    return null;
  }

  const rawValue = window.localStorage.getItem(storageKey);

  if (!rawValue) {
    return null;
  }

  try {
    return JSON.parse(rawValue) as TablePreferences<TColumn>;
  } catch {
    return null;
  }
}
